import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { ClientPortalApiService } from '../../services/client-portal-api.service';
import { AuthStateService } from '@hypenation/auth';
import { OrganiserDto } from '../../models/client-portal.models';
import { environment } from 'environments/environment';

@Component({
  selector: 'hypenation-organisers',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    NzSpinModule,
    NzIconModule,
    NzButtonModule,
    NzEmptyModule,
  ],
  templateUrl: './organisers.component.html',
  styleUrl: './organisers.component.css',
})
export class OrganisersComponent implements OnInit {
  loading = false;
  organisers: OrganiserDto[] = [];
  favouriteUids = new Set<string>();
  favouriteLoading = new Set<string>();

  private readonly apiService = inject(ClientPortalApiService);
  private readonly authStateService = inject(AuthStateService);
  private readonly notification = inject(NzNotificationService);

  get organiserSignupUrl(): string {
    return environment.organiserPortalUrl + '#/auth/signup';
  }

  get organiserLoginUrl(): string {
    return environment.organiserPortalUrl + '#/auth/login';
  }

  private get userEmail(): string | null {
    return this.authStateService.getUserEmail();
  }

  ngOnInit(): void {
    this.loadOrganisers();
    this.loadFavourites();
  }

  loadOrganisers(): void {
    this.loading = true;
    this.apiService.getPublicOrganisers().subscribe({
      next: (res) => {
        this.organisers = res.body ?? [];
        this.loading = false;
      },
      error: () => (this.loading = false),
    });
  }

  loadFavourites(): void {
    if (!this.userEmail) return;
    this.apiService.getMyFavouriteOrganiserProfiles(this.userEmail).subscribe({
      next: (res) => {
        const favs = res.body ?? [];
        this.favouriteUids = new Set(favs.map(o => o.uid));
      },
    });
  }

  isFavourite(uid: string): boolean {
    return this.favouriteUids.has(uid);
  }

  isFavouriteLoading(uid: string): boolean {
    return this.favouriteLoading.has(uid);
  }

  toggleFavourite(organiser: OrganiserDto, e: MouseEvent): void {
    e.stopPropagation();
    e.preventDefault();
    if (!this.userEmail) return;

    if (this.isFavourite(organiser.uid)) {
      this.removeFavourite(organiser);
    } else {
      this.addFavourite(organiser);
    }
  }

  private addFavourite(organiser: OrganiserDto): void {
    if (!this.userEmail) return;
    this.favouriteLoading.add(organiser.uid);
    this.apiService.addFavouriteOrganiser(organiser.uid, this.userEmail).subscribe({
      next: () => {
        this.favouriteUids.add(organiser.uid);
        this.favouriteLoading.delete(organiser.uid);
        this.notification.success('Following', `You are now following ${organiser.name}.`);
      },
      error: () => {
        this.favouriteLoading.delete(organiser.uid);
        this.notification.error('Error', 'Could not follow organiser. Try again.');
      },
    });
  }

  private removeFavourite(organiser: OrganiserDto): void {
    if (!this.userEmail) return;
    this.favouriteLoading.add(organiser.uid);
    this.apiService.removeFavouriteOrganiser(organiser.uid, this.userEmail).subscribe({
      next: () => {
        this.favouriteUids.delete(organiser.uid);
        this.favouriteLoading.delete(organiser.uid);
        this.notification.success('Unfollowed', `You unfollowed ${organiser.name}.`);
      },
      error: () => {
        this.favouriteLoading.delete(organiser.uid);
        this.notification.error('Error', 'Could not unfollow organiser. Try again.');
      },
    });
  }

  getInitial(name: string): string {
    return name ? name.charAt(0).toUpperCase() : '?';
  }
}
